import React, { useMemo, useEffect, useState } from 'react';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import { Checkbox, InputNumber, Slider, Tooltip } from 'antd';
import { IBudgetRange, IRange } from '@share/common-types';
import { getBudgetRangeLabel } from '@share/utils';
import { ACTIVITY_BUDGET_KEY_ALIASES } from '@utils';

import { USE_LEGACY_BUDGET_CHECKBOX_MODE } from './legacy-mode';
import './style.scss';

const rangeToKey = (r: IRange): string =>
  `${r.from ?? 'null'}-${r.to ?? 'null'}`;

const sortBudgetRanges = (ranges: IBudgetRange[]): IBudgetRange[] =>
  [...ranges].sort((a, b) => {
    const af = a.key.from ?? -Infinity;
    const bf = b.key.from ?? -Infinity;
    if (af !== bf) return af - bf;
    const at = a.key.to ?? Infinity;
    const bt = b.key.to ?? Infinity;
    return at - bt;
  });

const hiBound = (r: IRange): number =>
  r.to ?? Number.MAX_SAFE_INTEGER;

const loBound = (r: IRange): number =>
  r.from ?? 0;

const formatHistogramBarTooltip = (intl: IntlShape, key: IRange, count: number): string => {
  const fromN = key.from ?? 0;
  const from = `$${fromN}`;
  if (key.to == null) {
    return intl.formatMessage(
      {
        id: 'filter.budget.histogram.open',
        defaultMessage: 'From {from} and up. {bookings, plural, one {# booking} other {# bookings}}',
      },
      { from, bookings: count },
    );
  }
  return intl.formatMessage(
    {
      id: 'filter.budget.histogram.range',
      defaultMessage: 'From {from} to {to}. {bookings, plural, one {# booking} other {# bookings}}',
    },
    { from, to: `$${key.to}`, bookings: count },
  );
};

/** Slider tooltip: lower bound only (no "from–to" range). */
const formatBudgetBucketTooltip = (key: IRange): string => {
  if (!key) return '';
  const left = key.from ?? 0;
  return `$${left}`;
};

/** First bucket index whose upper bound still reaches `price` (slider / min input). */
const findLeftIndexForPrice = (price: number, buckets: IBudgetRange[]): number => {
  for (let i = 0; i < buckets.length; i++) {
    if (price <= hiBound(buckets[i].key)) return i;
  }
  return Math.max(0, buckets.length - 1);
};

/** Last bucket index whose lower bound is at or below `price` (max input). */
const findRightIndexForPrice = (price: number, buckets: IBudgetRange[]): number => {
  for (let j = buckets.length - 1; j >= 0; j--) {
    if (price >= loBound(buckets[j].key)) return j;
  }
  return 0;
};

const resolveBudgetIndex = (key: string, indexByKey: Map<string, number>): number | undefined => {
  let idx = indexByKey.get(key);
  if (idx !== undefined) return idx;
  const mapped = ACTIVITY_BUDGET_KEY_ALIASES[key];
  if (mapped !== undefined) return indexByKey.get(mapped);
  return undefined;
};

const indicesFromBudget = (
  budget: string[],
  buckets: IBudgetRange[],
): [number, number] => {
  const n = buckets.length;
  if (n === 0) return [0, 0];
  if (!budget?.length) return [0, n - 1];

  const indexByKey = new Map(buckets.map((b, idx) => [rangeToKey(b.key), idx]));
  const indices = budget
    .map((k) => resolveBudgetIndex(k, indexByKey))
    .filter((x): x is number => x !== undefined);

  if (!indices.length) return [0, n - 1];

  return [Math.min(...indices), Math.max(...indices)];
};

const budgetFromIndices = (
  left: number,
  right: number,
  buckets: IBudgetRange[],
): string[] => {
  const n = buckets.length;
  if (n === 0) return [];
  const lo = Math.max(0, Math.min(left, right));
  const hi = Math.min(n - 1, Math.max(left, right));
  if (lo === 0 && hi === n - 1) return [];
  return buckets.slice(lo, hi + 1).map((b) => rangeToKey(b.key));
};

export interface IFilterBudgetRangeProps {
  budgetRanges: IBudgetRange[];
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Wider layout for "All filters" drawer */
  layout?: 'compact' | 'comfortable';
  /**
   * Optional title rendered inside the component. Prefer a parent `<p className="result-wrapper__filters-tittle">` + `FormattedMessage` for the main "Budget" heading.
   * Pass `false` or `null` to hide the internal title.
   */
  titleMessageId?: string | false | null;
}

export const FilterBudgetRange: React.FC<IFilterBudgetRangeProps> = ({
  budgetRanges,
  values,
  onChange,
  disabled = false,
  layout = 'compact',
  titleMessageId,
}) => {
  const intl = useIntl();
  const resolvedTitleId =
    titleMessageId === false || titleMessageId == null
      ? null
      : typeof titleMessageId === 'string' && titleMessageId.length > 0
        ? titleMessageId
        : null;
  const buckets = useMemo(() => {
    const withCounts = (budgetRanges || []).filter(
      (b) =>
        b.count > 0 ||
        (b.key.from === 500 && b.key.to === 500),
    );
    return sortBudgetRanges(withCounts);
  }, [budgetRanges]);

  const [sliderRange, setSliderRange] = useState<[number, number]>(() =>
    indicesFromBudget(values, buckets),
  );

  useEffect(() => {
    const len = buckets.length;
    if (!len) return;
    let [lo, hi] = indicesFromBudget(values, buckets);
    lo = Math.min(Math.max(0, lo), len - 1);
    hi = Math.min(Math.max(0, hi), len - 1);
    setSliderRange([lo, hi]);
  }, [values, buckets]);

  const n = buckets.length;
  const maxCount = useMemo(
    () => (n ? Math.max(...buckets.map((b) => b.count), 1) : 1),
    [buckets, n],
  );

  const globalLo = useMemo(() => {
    if (!n) return 0;
    return Math.min(...buckets.map((b) => loBound(b.key)));
  }, [buckets, n]);

  const globalHi = useMemo(() => {
    if (!n) return 0;
    return Math.max(...buckets.map((b) => Math.min(hiBound(b.key), Number.MAX_SAFE_INTEGER)));
  }, [buckets, n]);

  const leftIdx = sliderRange[0];
  const rightIdx = sliderRange[1];

  /** Slider state can be stale when bucket count changes (e.g. counters load). */
  const safeLeftIdx = n ? Math.min(Math.max(0, leftIdx), n - 1) : 0;
  const safeRightIdx = n ? Math.min(Math.max(0, rightIdx), n - 1) : 0;

  const commitRange = (next: [number, number]) => {
    if (!n) return;
    let lo = Math.min(next[0], next[1]);
    let hi = Math.max(next[0], next[1]);
    lo = Math.max(0, Math.min(lo, n - 1));
    hi = Math.max(0, Math.min(hi, n - 1));
    setSliderRange([lo, hi]);
    onChange(budgetFromIndices(lo, hi, buckets));
  };

  /** One bucket only (histogram bar click). */
  const selectSingleBucket = (index: number) => {
    if (disabled || !n) return;
    const i = Math.max(0, Math.min(index, n - 1));
    setSliderRange([i, i]);
    onChange([rangeToKey(buckets[i].key)]);
  };

  const displayMinNumber = (): number => {
    if (!n) return globalLo;
    return loBound(buckets[safeLeftIdx].key);
  };

  const displayMaxNumber = (): number => {
    if (!n) return globalHi;
    const k = buckets[safeRightIdx].key;
    if (k.to == null && k.from != null) {
      if (k.from === 501 || k.from === 500) return 500;
      return k.from;
    }
    return hiBound(k);
  };

  const maxBucketKey = n ? buckets[safeRightIdx]?.key : undefined;
  const maxHasPlus =
    !!maxBucketKey &&
    maxBucketKey.to == null &&
    maxBucketKey.from != null &&
    (maxBucketKey.from === 501 || maxBucketKey.from === 500);

  const selectedKeySet = useMemo(() => new Set(values || []), [values]);

  const handleMinInput = (v: number | null) => {
    if (v == null || !n || Number.isNaN(v)) return;
    const clamped = Math.min(Math.max(v, globalLo), globalHi);
    const li = findLeftIndexForPrice(clamped, buckets);
    const ri = safeRightIdx;
    commitRange([Math.min(li, ri), ri]);
  };

  const handleMaxInput = (v: number | null) => {
    if (v == null || !n || Number.isNaN(v)) return;
    const clamped = Math.min(Math.max(v, globalLo), globalHi);
    const ri = findRightIndexForPrice(clamped, buckets);
    const li = safeLeftIdx;
    commitRange([li, Math.max(li, ri)]);
  };

  if (!n) return null;

  if (USE_LEGACY_BUDGET_CHECKBOX_MODE) {
    return (
      <div
        className={`filter-budget-range filter-budget-range--${layout} filter-budget-range--checkbox-list${
          disabled ? ' filter-budget-range--disabled' : ''
        }`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {resolvedTitleId ? (
          <p className="filter-budget-range__title filter__title secondary-font">
            <FormattedMessage id={resolvedTitleId} />
          </p>
        ) : null}
        <div className="filter filter-checkbox exclude-border filter-budget-range__checkbox-list">
          <Checkbox.Group value={values} onChange={onChange} disabled={disabled}>
            {buckets.map((b) => {
              const k = rangeToKey(b.key);
              return (
                <div key={k}>
                  <Checkbox value={k} disabled={disabled || b.disabled}>
                    <span className="filter__option-name">
                      {getBudgetRangeLabel(b.key)}
                      {b.count > 0 && <strong> ({b.count})</strong>}
                    </span>
                  </Checkbox>
                </div>
              );
            })}
          </Checkbox.Group>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`filter-budget-range filter-budget-range--${layout}${disabled ? ' filter-budget-range--disabled' : ''}`}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {resolvedTitleId ? (
        <p className="filter-budget-range__title filter__title secondary-font">
          <FormattedMessage id={resolvedTitleId} />
        </p>
      ) : null}
      <div className="filter-budget-range__inputs">
        <label className="filter-budget-range__field">
          <span className="filter-budget-range__field-label">
            <FormattedMessage id="filter.budget.min" defaultMessage="Min" />
          </span>
          <InputNumber
            className="filter-budget-range__input"
            prefix="$"
            size="small"
            min={globalLo}
            max={globalHi}
            controls={false}
            disabled={disabled}
            value={displayMinNumber()}
            onChange={handleMinInput}
          />
        </label>
        <label className="filter-budget-range__field">
          <span className="filter-budget-range__field-label">
            <FormattedMessage id="filter.budget.max" defaultMessage="Max" />
          </span>
          <div
            className={`filter-budget-range__input-wrap${maxHasPlus ? ' filter-budget-range__input-wrap--with-plus' : ''}`}
          >
            <InputNumber
              className="filter-budget-range__input"
              prefix="$"
              size="small"
              min={globalLo}
              max={globalHi}
              controls={false}
              disabled={disabled}
              value={displayMaxNumber()}
              onChange={handleMaxInput}
            />
            {maxHasPlus ? <span className="filter-budget-range__plus-suffix">+</span> : null}
          </div>
        </label>
      </div>

      <div className="filter-budget-range__slider-block">
        <div className="filter-budget-range__histogram">
          {buckets.map((b, i) => {
            const h = maxCount > 0 ? Math.round((b.count / maxCount) * 100) : 0;
            const k = rangeToKey(b.key);
            const inSpan = !values?.length
              ? i >= safeLeftIdx && i <= safeRightIdx
              : selectedKeySet.has(k);
            const barLabel = formatHistogramBarTooltip(intl, b.key, b.count);
            return (
              <Tooltip key={k} title={barLabel} mouseEnterDelay={0.15}>
                <div
                  className={`filter-budget-range__hist-cell${
                    disabled ? ' filter-budget-range__hist-cell--disabled' : ' filter-budget-range__hist-cell--clickable'
                  }`}
                  onClick={
                    disabled
                      ? undefined
                      : (e) => {
                          e.stopPropagation();
                          selectSingleBucket(i);
                        }
                  }
                  onKeyDown={
                    disabled
                      ? undefined
                      : (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            selectSingleBucket(i);
                          }
                        }
                  }
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-label={barLabel}
                  aria-pressed={values?.length === 1 ? selectedKeySet.has(k) : undefined}
                >
                  <div
                    className={`filter-budget-range__hist-bar ${inSpan ? 'is-in-range' : ''}`}
                    style={{ height: `${Math.max(h, 4)}%` }}
                  />
                </div>
              </Tooltip>
            );
          })}
        </div>
        <Slider
          range
          min={0}
          max={n - 1}
          step={1}
          disabled={disabled}
          value={[safeLeftIdx, safeRightIdx]}
          tooltip={{
            formatter: (idx) => {
              if (idx == null || !n) return '';
              const i = Math.min(Math.max(0, idx as number), n - 1);
              return formatBudgetBucketTooltip(buckets[i]?.key);
            },
          }}
          onChange={(v) => {
            const pair = v as [number, number];
            setSliderRange(pair);
          }}
          onAfterChange={(v) => {
            const pair = v as [number, number];
            commitRange(pair);
          }}
        />
      </div>
    </div>
  );
};
